import React, { useState, useRef, useCallback } from 'react';
import { appendScope, createLedgerEntry } from '../api';
import { useAuth } from '../context/AuthContext';
import { Plus, ShieldCheck } from './Icons';
import './ScopeEditor.css';

const QUICK_CHIPS = [
  { label: '3 extra revisions +$300' },
  { label: 'Expedited delivery — 2 weeks' },
  { label: 'Social media excluded' },
  { label: 'Maintenance not included' },
  { label: 'Rush fee agreed at $150/hr' },
];

export default function ScopeEditor({ projectId, onScopeUpdated, locked = false }) {
  const { planMeta } = useAuth();
  const hasLedger = planMeta?.ledger === true;

  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const textareaRef = useRef(null);

  const canSubmit = text.trim().length > 0 && !loading;

  const handleAppend = useCallback(async () => {
    if (!canSubmit) return;
    setLoading(true);
    setError('');
    setSuccess(false);
    try {
      const updated = await appendScope(projectId, text);
      if (onScopeUpdated) onScopeUpdated(updated);

      // For Pro/Teams users: silently also log this as a change_order in the Scope Ledger
      // so one action keeps both AI context AND the financial audit trail in sync.
      if (hasLedger) {
        createLedgerEntry({
          project_id: projectId,
          type: 'change_order',
          description: text.trim(),
          status: 'noted',
        }).catch(() => {}); // silently fail — doesn't block the main flow
      }

      setText('');
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2500);
    } catch (err) {
      console.error(err);
      setError('Failed to save — please try again.');
    } finally {
      setLoading(false);
    }
  }, [canSubmit, projectId, text, onScopeUpdated, hasLedger]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleAppend();
  }, [handleAppend]);

  const handleUseChip = useCallback((label) => {
    setText(label);
    textareaRef.current?.focus();
  }, []);

  // Locked state for free users
  if (locked) {
    return (
      <div className="scope-editor-wrap scope-editor-locked">
        <div className="se-lock-icon"><ShieldCheck size={20} /></div>
        <div className="se-lock-content">
          <div className="se-title">Scope Ledger</div>
          <p className="se-subtitle">Appending custom scope changes is a <strong>Pro</strong> feature. Upgrade to log new agreements, amendments, and scope changes.</p>
        </div>
        <button className="btn btn-primary" style={{ flexShrink: 0, fontSize: '0.82rem', padding: '8px 16px' }}>
          Upgrade to Pro
        </button>
      </div>
    );
  }

  return (
    <div className="scope-editor-wrap">
      {/* Header row */}
      <div className="se-header">
        <div className="se-header-left">
          <div className="se-icon"><Plus size={16} /></div>
          <div>
            <div className="se-title">Log a Scope Change</div>
            <div className="se-subtitle">
              New agreement from a call or email? Add it here — the AI will factor it into all evaluations.
            </div>
          </div>
        </div>
      </div>

      {/* Input area */}
      <div className={`se-input-wrap ${text.trim() ? 'se-input-active' : ''}`}>
        <textarea
          ref={textareaRef}
          className="se-textarea"
          rows={2}
          placeholder="What did you agree to? e.g. Client confirmed 2 extra revision rounds for $300…"
          value={text}
          onChange={e => { setText(e.target.value); setError(''); }}
          onKeyDown={handleKeyDown}
          disabled={loading}
        />
        <div className="se-input-footer">
          <span className="se-hint">
            {text.trim() ? '⌘ Enter to save' : 'Type or pick a quick example below'}
          </span>
          <button
            className={`se-submit-btn ${canSubmit ? 'se-submit-ready' : ''} ${success ? 'se-submit-success' : ''}`}
            onClick={handleAppend}
            disabled={!canSubmit}
          >
            {loading ? (
              <span className="se-spinner" />
            ) : success ? (
              <>Logged!</>
            ) : (
              <>Log Change{hasLedger ? ' + Ledger' : ''}</>
            )}
          </button>
        </div>
      </div>

      {error && <div className="se-error">{error}</div>}

      {/* Quick chips */}
      <div className="se-chips-row">
        <span className="se-chips-label">Quick add:</span>
        <div className="se-chips">
          {QUICK_CHIPS.map((c, i) => (
            <button key={i} className="se-chip" onClick={() => handleUseChip(c.label)}>
              {c.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
